/**
 * WebsiteTech Growth Agent — Phase 1 vertical slice.
 *
 * Four pages in one file (small, focused components):
 *   /grow                              — Website URL intake (hero + 3-field form)
 *   /grow/dashboard/:businessId        — 10-tile Growth Dashboard + BGP + Campaign CTA
 *   /grow/approvals                    — Approval Center (Draft-mode gate)
 *   /grow/leads                        — Unified Lead Inbox (channel-tagged + intent classifier)
 *
 * Opinionated UX: left-aligned, generous spacing, one primary CTA per view,
 * no "collection of SaaS modules" — one growth loop.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight, Loader2, Globe, Target, DollarSign, CheckCircle2, AlertTriangle,
  Rocket, Shield, MessageSquare, Zap, Sparkles, Circle, Clock, RotateCw,
  Mail, Phone, FileText, Bot, Smartphone, Monitor, Megaphone, QrCode,
  ThumbsUp, ThumbsDown, Flame, HelpCircle, UserCheck, XCircle, Ban, Eye,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =============================================================================
// Shared chrome
// =============================================================================
function GrowShell({ children, active = null }) {
  const nav = [
    { to: "/grow",              label: "Overview",   key: "overview" },
    { to: "/grow/approvals",    label: "Approvals",  key: "approvals" },
    { to: "/grow/leads",        label: "Leads",      key: "leads" },
  ];
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center gap-6">
          <Link to="/grow" className="font-heading font-extrabold text-slate-900 text-lg">
            <span className="text-emerald-600">●</span> WebsiteTech <span className="text-slate-400 font-semibold">Growth Agent</span>
          </Link>
          <nav className="ml-auto flex items-center gap-5">
            {nav.map((n) => (
              <Link key={n.key} to={n.to} className={`text-sm ${active === n.key ? "text-emerald-700 font-bold" : "text-slate-500 hover:text-slate-900"}`}>
                {n.label}
              </Link>
            ))}
            <Link to="/" className="text-xs text-slate-400 hover:text-slate-700">← back to main site</Link>
          </nav>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
}

// =============================================================================
// /grow — Intake
// =============================================================================
export function GrowIntake() {
  const [url, setUrl] = useState("");
  const [goal, setGoal] = useState("");
  const [area, setArea] = useState("");
  const [budget, setBudget] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!url || url.length < 4) { toast.error("Please enter a website URL"); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/grow/onboard`, {
        website_url: url, primary_goal: goal, service_area: area, monthly_budget_usd: Number(budget || 0),
      });
      toast.success(data.resumed ? "Resuming your growth plan…" : "Growth profile ready");
      nav(`/grow/dashboard/${data.business_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GrowShell active="overview">
      <section className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-100">Website → Customers · in one loop</p>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight mt-4">
              Enter your website.<br />
              <span className="text-amber-200">We'll find ways to grow your business.</span>
            </h1>
            <p className="mt-5 text-lg text-white/90 max-w-2xl">
              Analyse. Improve. Reach. Convert. Grow. WebsiteTech turns your site into a growth operating system — SEO, campaigns, portal ads, AI follow-up, measured ROI.
            </p>
            <form onSubmit={onSubmit} className="mt-9 bg-white rounded-2xl p-5 shadow-2xl text-slate-900 max-w-2xl" data-testid="grow-intake-form">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Globe size={13} /> Your website URL</label>
              <input
                type="text" required value={url} onChange={(e) => setUrl(e.target.value)}
                data-testid="grow-url"
                placeholder="acme.com"
                className="w-full mt-1 border-b-2 border-slate-200 focus:border-emerald-500 outline-none py-2 text-lg font-semibold"
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Target size={11} /> Primary goal (optional)</label>
                  <input type="text" value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="grow-goal" placeholder="More qualified leads" className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Globe size={11} /> Service area (optional)</label>
                  <input type="text" value={area} onChange={(e) => setArea(e.target.value)} placeholder="India / US / Global" className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><DollarSign size={11} /> Monthly budget USD (optional)</label>
                  <input type="number" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="500" className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 outline-none" />
                </div>
              </div>
              <button
                type="submit" disabled={submitting} data-testid="grow-submit"
                className="mt-6 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold px-6 py-3 rounded-xl inline-flex items-center gap-2 w-full justify-center"
              >
                {submitting ? <><Loader2 className="animate-spin" size={16} /> Reading your site… (10-30s)</> : <>Analyse my website <ArrowRight size={16} /></>}
              </button>
              <p className="text-[11px] text-slate-400 mt-3 text-center">We only read publicly-visible pages. Nothing publishes or sends until you approve.</p>
            </form>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-extrabold text-slate-900 text-3xl text-center">One loop. No module soup.</h2>
          <div className="mt-9 grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { icon: Eye,        label: "Analyze",   note: "Audit + BGP" },
              { icon: Rocket,     label: "Improve",   note: "SEO + content" },
              { icon: Target,     label: "Find",      note: "Audiences" },
              { icon: Sparkles,   label: "Create",    note: "Campaigns" },
              { icon: Shield,     label: "Approve",   note: "Draft mode" },
              { icon: Megaphone,  label: "Reach",     note: "Email + WhatsApp + ads" },
              { icon: UserCheck,  label: "Convert",   note: "Lead inbox" },
              { icon: MessageSquare, label: "Follow up", note: "AI drafts" },
              { icon: Zap,        label: "Measure",   note: "Splink + QR" },
              { icon: RotateCw,   label: "Optimize",  note: "Next-best action" },
            ].map((s) => {
              const I = s.icon;
              return (
                <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                  <I size={22} className="mx-auto text-emerald-600" />
                  <div className="font-bold text-slate-900 text-sm mt-2">{s.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{s.note}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </GrowShell>
  );
}

// =============================================================================
// /grow/dashboard/:businessId — Growth Dashboard
// =============================================================================
const TILE_ICONS = {
  health: Shield, seo: Target, content: FileText, audiences: UserCheck,
  campaigns: Sparkles, creative: Sparkles, active: Rocket, leads: MessageSquare,
  conversions: ThumbsUp, roi: Zap,
};

export function GrowDashboard() {
  const { businessId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [drafting, setDrafting] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/grow/dashboard/${businessId}`);
      setData(data); setState("ok");
    } catch { setState("error"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);
  useEffect(() => { if (businessId) localStorage.setItem("grow.lastBusinessId", businessId); }, [businessId]);

  const draftCampaign = async () => {
    setDrafting(true);
    try {
      const { data } = await axios.post(`${API}/grow/campaign/draft`, { business_id: businessId, channel: "email" });
      toast.success("Draft ready. Open Approvals to review and approve.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not draft campaign");
    }
    setDrafting(false);
  };

  if (state === "loading") return <GrowShell><div className="py-24 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div></GrowShell>;
  if (state === "error" || !data) return <GrowShell><div className="py-20 text-center"><AlertTriangle className="mx-auto text-amber-500" /><p className="mt-3 text-slate-600">Could not load this business. <Link to="/grow" className="text-emerald-700 font-bold">Start again →</Link></p></div></GrowShell>;

  const biz = data.business;
  const bgp = biz.bgp || {};
  const inferred = Object.entries(bgp).filter(([k, v]) =>
    (Array.isArray(v) ? v.some((x) => x?.source === "inference") : v?.source === "inference")
  );

  return (
    <GrowShell active="overview">
      {/* Header strip */}
      <section className="bg-white border-b border-slate-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500">Business</p>
            <h1 className="font-heading font-extrabold text-slate-900 text-2xl truncate" data-testid="dashboard-business-url">{biz.website_url}</h1>
            <p className="text-sm text-slate-500 mt-1">{biz.primary_goal || "No goal set"} · {biz.service_area || "No service area"} · ${biz.monthly_budget_usd || 0}/mo budget</p>
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-bold">● Draft mode (Level 1)</span>
            {data.approvals_open > 0 && (
              <Link to="/grow/approvals" className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-bold inline-flex items-center gap-1">
                <Clock size={12} /> {data.approvals_open} pending
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* 10-tile dashboard */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="dashboard-tiles">
            {data.tiles.map((t) => {
              const I = TILE_ICONS[t.key] || Circle;
              return (
                <div key={t.key} data-testid={`tile-${t.key}`} className="bg-white border border-slate-200 rounded-xl p-4">
                  <I size={18} className="text-emerald-600" />
                  <div className="font-heading font-extrabold text-slate-900 text-2xl mt-2">{t.value}<span className="text-xs font-semibold text-slate-400 ml-1">{t.unit}</span></div>
                  <div className="text-xs text-slate-500 mt-1">{t.label}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={draftCampaign} disabled={drafting} data-testid="dashboard-draft-campaign" className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-xl text-sm inline-flex items-center gap-2">
              {drafting ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
              Draft a campaign
            </button>
            <Link to="/grow/approvals" className="bg-white border border-slate-300 hover:border-emerald-500 text-slate-700 font-bold px-5 py-2.5 rounded-xl text-sm inline-flex items-center gap-2">
              <Shield size={14} /> Open approvals
            </Link>
            <Link to="/grow/leads" className="bg-white border border-slate-300 hover:border-emerald-500 text-slate-700 font-bold px-5 py-2.5 rounded-xl text-sm inline-flex items-center gap-2">
              <MessageSquare size={14} /> Lead inbox
            </Link>
            <Link to="/grow/audiences" className="bg-white border border-slate-300 hover:border-emerald-500 text-slate-700 font-bold px-5 py-2.5 rounded-xl text-sm inline-flex items-center gap-2" data-testid="dashboard-audiences-cta">
              <UserCheck size={14} /> Audiences
            </Link>
            <Link to={`/grow/channels/${businessId}`} className="bg-white border border-slate-300 hover:border-emerald-500 text-slate-700 font-bold px-5 py-2.5 rounded-xl text-sm inline-flex items-center gap-2" data-testid="dashboard-channels-cta">
              <Shield size={14} /> Channels & autopilot
            </Link>
          </div>
        </div>
      </section>

      {/* Business Growth Profile */}
      <section className="py-10 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-extrabold text-slate-900 text-2xl">Business Growth Profile</h2>
          <p className="text-sm text-slate-500 mt-1">What we found from your site. Confirm facts marked <span className="text-amber-600 font-semibold">inference</span> before using them in outbound.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" data-testid="bgp-grid">
            {Object.entries(bgp).map(([k, v]) => <BgpCard key={k} field={k} value={v} />)}
          </div>
          {inferred.length === 0 && Object.keys(bgp).length > 0 && (
            <div className="mt-6 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5">
              <CheckCircle2 size={16} /> All facts are directly observed — safe to use in outbound.
            </div>
          )}
        </div>
      </section>
    </GrowShell>
  );
}

function BgpCard({ field, value }) {
  const label = field.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  const items = Array.isArray(value) ? value : (value ? [value] : []);
  if (!items.length) return null;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-2 space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-start gap-2">
            <SourceBadge source={it?.source} />
            <span className="text-sm text-slate-800 flex-1 leading-relaxed">{it?.value || JSON.stringify(it)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SourceBadge({ source }) {
  const map = {
    confirmed_fact: { label: "fact", cls: "bg-emerald-100 text-emerald-700" },
    inference:      { label: "inferred", cls: "bg-amber-100 text-amber-700" },
    needs_input:    { label: "needs input", cls: "bg-rose-100 text-rose-700" },
  };
  const m = map[source] || { label: source || "?", cls: "bg-slate-100 text-slate-500" };
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${m.cls} shrink-0 mt-0.5`}>{m.label}</span>;
}

// =============================================================================
// /grow/approvals — Approval Center
// =============================================================================
export function GrowApprovals() {
  const [items, setItems] = useState(null);
  const [acting, setActing] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/grow/approvals?status=pending`);
      setItems(data.items);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    setActing(id);
    try {
      await axios.post(`${API}/grow/approvals/decide`, { approval_id: id, decision });
      toast.success(decision === "approve" ? "Approved — campaign is now active" : "Rejected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record decision");
    }
    setActing(null);
  };

  return (
    <GrowShell active="approvals">
      <section className="py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading font-extrabold text-slate-900 text-3xl">Approvals</h1>
          <p className="text-sm text-slate-500 mt-1">Nothing publishes, sends, or spends money until you approve it. Default mode: Draft (Level 1).</p>

          <div className="mt-8 space-y-3" data-testid="approvals-list">
            {items === null && <div className="text-center py-10 text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>}
            {items && items.length === 0 && (
              <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center">
                <CheckCircle2 size={32} className="mx-auto text-emerald-500" />
                <h3 className="font-heading font-bold text-slate-900 mt-3">All clear</h3>
                <p className="text-sm text-slate-500 mt-1">No pending approvals.</p>
              </div>
            )}
            {items && items.map((a) => (
              <div key={a.id} data-testid={`approval-${a.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 flex gap-4 items-center">
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><Clock size={18} /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">{a.object_type}</span>
                    <span className="text-[10px] text-slate-400">by {a.requested_by}</span>
                  </div>
                  <div className="font-bold text-slate-900 mt-1 truncate">{a.summary}</div>
                  <div className="text-xs text-slate-400 mt-0.5">Business: {a.business_id}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => decide(a.id, "reject")} disabled={acting === a.id} data-testid={`reject-${a.id}`}
                    className="bg-white border border-slate-300 hover:border-rose-400 text-slate-700 hover:text-rose-700 font-bold px-3 py-2 rounded-lg text-xs inline-flex items-center gap-1">
                    <ThumbsDown size={13} /> Reject
                  </button>
                  <button onClick={() => decide(a.id, "approve")} disabled={acting === a.id} data-testid={`approve-${a.id}`}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-2 rounded-lg text-xs inline-flex items-center gap-1">
                    {acting === a.id ? <Loader2 className="animate-spin" size={13} /> : <ThumbsUp size={13} />} Approve
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </GrowShell>
  );
}

// =============================================================================
// /grow/leads — Unified Lead Inbox
// =============================================================================
const CHANNEL_ICON = {
  email: Mail, whatsapp: Smartphone, form: FileText, "ai-chat": Bot,
  phone: Phone, "portal-ad": Monitor, splink: QrCode, qr: QrCode,
};
const INTENT_LABEL = {
  hot:            { icon: Flame,     cls: "bg-rose-100 text-rose-700",     label: "Hot" },
  interested:     { icon: ThumbsUp,  cls: "bg-emerald-100 text-emerald-700", label: "Interested" },
  "needs-info":   { icon: HelpCircle, cls: "bg-blue-100 text-blue-700",    label: "Needs info" },
  later:          { icon: Clock,     cls: "bg-slate-100 text-slate-600",   label: "Later" },
  "not-interested": { icon: ThumbsDown, cls: "bg-slate-100 text-slate-500", label: "Not interested" },
  unsubscribe:    { icon: Ban,       cls: "bg-rose-50 text-rose-500",       label: "Unsubscribe" },
  invalid:        { icon: XCircle,   cls: "bg-slate-100 text-slate-400",   label: "Invalid" },
  "human-review": { icon: UserCheck, cls: "bg-amber-100 text-amber-700",   label: "Review" },
  unknown:        { icon: Circle,    cls: "bg-slate-100 text-slate-400",   label: "Unclassified" },
};

export function GrowLeads() {
  const [items, setItems] = useState(null);
  const [channel, setChannel] = useState("");
  const [classifying, setClassifying] = useState(null);

  const load = async () => {
    try {
      const q = channel ? `?channel=${channel}` : "";
      const { data } = await axios.get(`${API}/grow/leads${q}`);
      setItems(data.items);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [channel]);

  const classify = async (id) => {
    setClassifying(id);
    try {
      await axios.post(`${API}/grow/leads/${id}/classify`);
      await load();
    } catch { toast.error("Classifier unavailable"); }
    setClassifying(null);
  };

  const channels = useMemo(() => ["", ...Object.keys(CHANNEL_ICON)], []);

  return (
    <GrowShell active="leads">
      <section className="py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading font-extrabold text-slate-900 text-3xl">Lead Inbox</h1>
          <p className="text-sm text-slate-500 mt-1">Every inbound — email, WhatsApp, form, AI chat — on one timeline.</p>

          <div className="mt-5 flex gap-2 flex-wrap" data-testid="leads-channel-filters">
            {channels.map((c) => (
              <button key={c || "all"} onClick={() => setChannel(c)} data-testid={`filter-${c || "all"}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${channel === c ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}>
                {c ? (c.charAt(0).toUpperCase() + c.slice(1).replace("-", " ")) : "All channels"}
              </button>
            ))}
          </div>

          <div className="mt-6 space-y-2" data-testid="leads-list">
            {items === null && <div className="text-center py-10 text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>}
            {items && items.length === 0 && (
              <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center">
                <MessageSquare size={28} className="mx-auto text-slate-300" />
                <h3 className="font-heading font-bold text-slate-900 mt-3">No leads yet</h3>
                <p className="text-sm text-slate-500 mt-1">Approve a campaign and leads will land here.</p>
              </div>
            )}
            {items && items.map((ld) => {
              const Ch = CHANNEL_ICON[ld.channel] || FileText;
              const im = INTENT_LABEL[ld.intent] || INTENT_LABEL.unknown;
              const IM = im.icon;
              return (
                <div key={ld.id} data-testid={`lead-${ld.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><Ch size={16} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-900 truncate">{ld.name || "(no name)"}</span>
                      <span className="text-xs text-slate-400">· {ld.email || ld.phone || "no contact"}</span>
                      <span className="text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{ld.channel_label || ld.channel}</span>
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${im.cls}`}>
                        <IM size={9} /> {im.label}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 mt-1.5 line-clamp-2">{ld.message || "(no message)"}</p>
                    {ld.suggested_reply && (
                      <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-xs text-emerald-800">
                        <span className="font-bold">AI draft reply:</span> {ld.suggested_reply}
                      </div>
                    )}
                  </div>
                  {ld.intent === "unknown" && (
                    <button onClick={() => classify(ld.id)} disabled={classifying === ld.id} data-testid={`classify-${ld.id}`}
                      className="bg-white border border-slate-300 hover:border-emerald-500 text-slate-600 font-bold px-3 py-2 rounded-lg text-xs inline-flex items-center gap-1 shrink-0">
                      {classifying === ld.id ? <Loader2 className="animate-spin" size={12} /> : <Bot size={12} />} Classify
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </GrowShell>
  );
}
